import { ApiError } from "@/lib/api/api-error";
import { CancelError } from "@/lib/api/cancelable-promise";
import {
  AgentStreamConnectionError,
  AgentStreamProtocolError,
  AgentStreamTerminalError,
} from "@/services/agent-stream-errors";

export type AgentGatewayFailureKind =
  | "validation"
  | "session_expired"
  | "forbidden"
  | "missing"
  | "already_running"
  | "idempotency_conflict"
  | "rate_limited"
  | "upstream_failure"
  | "unavailable"
  | "timeout"
  | "cancelled"
  | "interrupted"
  | "unknown";

export type AgentGatewayFailure = {
  kind: AgentGatewayFailureKind;
  message: string;
  retryable: boolean;
  restoreDraft: boolean;
  refreshAccess: boolean;
};

type ConflictCode = "idempotency_conflict" | "request_already_running";

function getConflictCode(body: unknown): ConflictCode | undefined {
  if (
    typeof body !== "object" ||
    body === null ||
    !("code" in body) ||
    typeof body.code !== "string"
  ) {
    return undefined;
  }

  if (
    body.code === "idempotency_conflict" ||
    body.code === "request_already_running"
  ) {
    return body.code;
  }

  return undefined;
}

function isCancelledError(error: unknown): error is { isCancelled: true } {
  return (
    typeof error === "object" &&
    error !== null &&
    "isCancelled" in error &&
    error.isCancelled === true
  );
}

export function classifyAgentGatewayFailure(
  error: unknown,
): AgentGatewayFailure {
  if (error instanceof AgentStreamConnectionError) {
    return {
      kind: "interrupted",
      message:
        "The connection was interrupted. The session was refreshed and the message can be retried safely.",
      retryable: true,
      restoreDraft: false,
      refreshAccess: true,
    };
  }

  if (error instanceof AgentStreamTerminalError) {
    const kindByCode: Partial<Record<string, AgentGatewayFailureKind>> = {
      agent_session_not_found: "missing",
      agent_timeout: "timeout",
      agent_unavailable: "unavailable",
      agent_upstream_failure: "upstream_failure",
      stream_cancelled: "cancelled",
    };

    return {
      kind: kindByCode[error.code] ?? "upstream_failure",
      message: error.message,
      retryable: false,
      restoreDraft: false,
      refreshAccess: true,
    };
  }

  if (error instanceof AgentStreamProtocolError) {
    return {
      kind: "upstream_failure",
      message:
        "The message stream was interrupted. The session has been refreshed.",
      retryable: false,
      restoreDraft: false,
      refreshAccess: true,
    };
  }

  if (error instanceof CancelError || isCancelledError(error)) {
    return {
      kind: "cancelled",
      message: "Sending was interrupted. You can retry the message safely.",
      retryable: true,
      restoreDraft: false,
      refreshAccess: false,
    };
  }

  if (!(error instanceof ApiError)) {
    return {
      kind: "unknown",
      message:
        "The message could not be sent. Check your connection and try again.",
      retryable: true,
      restoreDraft: false,
      refreshAccess: false,
    };
  }

  switch (error.status) {
    case 400:
      return {
        kind: "validation",
        message: "Review your message and try sending it again.",
        retryable: false,
        restoreDraft: true,
        refreshAccess: false,
      };
    case 401:
      return {
        kind: "session_expired",
        message: "Your sign-in session expired. Please sign in again.",
        retryable: false,
        restoreDraft: false,
        refreshAccess: true,
      };
    case 403:
      return {
        kind: "forbidden",
        message: "You no longer have access to this agent or session.",
        retryable: false,
        restoreDraft: false,
        refreshAccess: true,
      };
    case 404:
      return {
        kind: "missing",
        message: "This agent session no longer exists or has expired.",
        retryable: false,
        restoreDraft: false,
        refreshAccess: true,
      };
    case 409:
      if (getConflictCode(error.body) === "request_already_running") {
        return {
          kind: "already_running",
          message:
            "This message is already being processed. Wait briefly, then retry.",
          retryable: true,
          restoreDraft: false,
          refreshAccess: false,
        };
      }

      return {
        kind: "idempotency_conflict",
        message:
          "This message cannot be retried safely. Edit and send it as a new message.",
        retryable: false,
        restoreDraft: true,
        refreshAccess: false,
      };
    case 429:
      return {
        kind: "rate_limited",
        message: "Too many requests were sent. Wait briefly, then retry.",
        retryable: true,
        restoreDraft: false,
        refreshAccess: false,
      };
    case 502:
      return {
        kind: "upstream_failure",
        message:
          "The agent returned an invalid response. You can retry the message.",
        retryable: true,
        restoreDraft: false,
        refreshAccess: false,
      };
    case 503:
      return {
        kind: "unavailable",
        message:
          "The agent service is temporarily unavailable. Try again shortly.",
        retryable: true,
        restoreDraft: false,
        refreshAccess: false,
      };
    case 504:
      return {
        kind: "timeout",
        message:
          "The agent took too long to respond. You can retry the message.",
        retryable: true,
        restoreDraft: false,
        refreshAccess: false,
      };
    default:
      return {
        kind: "unknown",
        message: "The message could not be sent. Please try again.",
        retryable: true,
        restoreDraft: false,
        refreshAccess: false,
      };
  }
}
